/**
 * Typisierte Hooks für `/api/v1/billing` (Spec 04a, `domains/billing`) — die
 * Sollstellung, ihre offenen Posten und die Kennzahl für die Symbolleiste.
 * Spec 04b ergänzt Zahlung erfassen und zuordnen.
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`; jede Mutation invalidiert über das Präfix
 * `keys.billing.all`, nie einzeln aufgezählt — Lauf, Nachzug, Handkorrektur
 * und Zahlung erfassen können `open-items`, `summary` **und** die
 * Sollstellungen eines Vertrags zugleich verändern.
 *
 * Die Mieterauswahl für den Filter „Mieter" (`open-items-filter-tenant`) und
 * für das Zahlungssheet (`payment-create-tenant`) gehört fachlich zu
 * `domains/tenants`, liegt aber hier noch einmal lokal: `features/billing`
 * kennt `features/contracts` nicht (`apps/web/CLAUDE.md`, „features kennen
 * einander nicht") — dieselbe Duplikation wie `useTenantOptions` in
 * `features/contracts/api.ts`.
 *
 * 04b baute drei Endpunkte: `GET /payment-methods`, `POST /payments`,
 * `POST /payments/{id}/allocations` (letzterer blieb ohne Hook — kein
 * Einstieg brauchte ihn, bis 04c den dritten Sheet-Einstieg baut). Spec 04c
 * ergänzt `GET /unallocated` (Reiter *Klärfälle*), `GET /tenants/{id}/ledger`
 * (Mietkonto) und `POST /payments/{id}/reverse` (Storno) — und liefert jetzt
 * auch den fehlenden Hook für `POST /payments/{id}/allocations`
 * (`useAddAllocations`), den der dritte Sheet-Einstieg „aus den Klärfällen"
 * braucht. Die Zuordnungsliste im Sheet (`payment-allocations`) verwendet
 * weiterhin `useOpenItems({ tenantId })`, das 04a bereits liefert — dafür
 * gibt es keinen eigenen `GET`-Endpunkt.
 *
 * Spec 08 ergänzt `GET /billing/matrix` (Reiter *Zahlungsmatrix*) — über
 * `useInfiniteQuery`, weil die Zeilen seitenweise angehängt werden
 * (`matrix-more`, AK-482), während `columns` und die Spaltensummen je Seite
 * identisch mitkommen (AK-479, Antwortform „Antwort" in der Spec).
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type OpenItem = components['schemas']['OpenItem']
export type OpenItemsPage = components['schemas']['OpenItemsPage']
export type BillingSummary = components['schemas']['BillingSummary']
export type ContractCharge = components['schemas']['ContractCharge']
export type ChargeUpdate = components['schemas']['ChargeUpdate']
export type ChargesGenerateResponse = components['schemas']['ChargesGenerateResponse']
export type LabeledValue = components['schemas']['LabeledValue']
export type PaymentCreate = components['schemas']['PaymentCreate']
export type PaymentDetail = components['schemas']['PaymentDetail']
export type PaymentAllocationsAdd = components['schemas']['PaymentAllocationsAdd']
export type PaymentReverseRequest = components['schemas']['PaymentReverseRequest']
export type UnallocatedPage = components['schemas']['UnallocatedPage']
export type UnallocatedPayment = components['schemas']['UnallocatedPayment']
export type LedgerPage = components['schemas']['LedgerPage']
export type LedgerRow = components['schemas']['LedgerRow']
export type MatrixPage = components['schemas']['MatrixPage']
export type MatrixColumn = components['schemas']['MatrixColumn']
export type MatrixRow = components['schemas']['MatrixRow']
export type MatrixCell = components['schemas']['MatrixCell']
export type UpcomingMonthsPage = components['schemas']['UpcomingMonthsPage']
export type PrepaidPage = components['schemas']['PrepaidPage']

/** Dieselbe schmale Form wie `TenantOption` in `features/contracts/api.ts`. */
export type TenantOption = components['schemas']['TenantListItem']

export interface ContractChargesPage {
  readonly items: ContractCharge[]
  readonly next_cursor: string | null
}

interface QueryOptions {
  readonly enabled?: boolean
}

interface OpenItemsFilter {
  readonly overdue?: boolean
  readonly tenantId?: string
  readonly scopeId?: string
}

/**
 * `GET /billing/open-items` — der Reiter *Offene Posten* (`open-items`,
 * ungefiltert bis auf das, was der Nutzer wählt) sowie, ungefiltert und ohne
 * eigene Anzeige, die Zuordnungsliste im `PaymentSheet` (`tenantId` gesetzt).
 * Bis Spec 07 trug sie mit `?limit=5` außerdem die Kurzfassung auf „Heute" —
 * der Parameter ist mit dem Bildschirm entfallen (AK-456), kein Aufrufer
 * setzt ihn mehr.
 */
export function useOpenItems(filter: OpenItemsFilter, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.billing.openItems({
      overdue: filter.overdue ?? false,
      tenantId: filter.tenantId,
      scopeId: filter.scopeId,
    }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (filter.overdue === true) params.set('overdue', 'true')
      if (filter.tenantId !== undefined) params.set('tenant_id', filter.tenantId)
      if (filter.scopeId !== undefined) params.set('scope_id', filter.scopeId)
      if (pageParam !== null) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<OpenItemsPage>(`/billing/open-items${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    select: (data): OpenItemsPage => {
      const first = data.pages[0]
      if (first === undefined) throw new Error('Die erste Seite der offenen Posten fehlt.')
      return {
        ...first,
        items: [...new Map(data.pages.flatMap((page) => page.items).map((item) => [item.charge_id, item])).values()],
        next_cursor: data.pages.at(-1)?.next_cursor ?? null,
      }
    },
    enabled: options.enabled ?? true,
  })
}

/** `GET /billing/summary` — die Kennzahlen der Sidebar `GeldSidebar` und der
 * Zähl-Endpunkt hinter `rail-geld-attention` (ADR 0011: „Kein Bereich ohne
 * eigenen Zähl-Endpunkt"). */
export function useBillingSummary(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.billing.summary,
    queryFn: ({ signal }) => request<BillingSummary>('/billing/summary', { signal }),
    enabled: options.enabled ?? true,
  })
}

/**
 * `GET /billing/contracts/{id}/charges` — die Sollstellungen eines
 * Vertrags. Läuft unbedingt, sobald ein Vertrag geöffnet ist (nicht erst im
 * Reiter *Geld*): Der Reiter *Stammdaten* braucht dasselbe Ergebnis, um zu
 * entscheiden, ob Miete und Nebenkosten gesperrt sind (Spec 04a, „Konditionen
 * wechseln zum Monatsersten": „sobald der Vertrag eine `charge` trägt") —
 * `ContractDetail` trägt dafür kein eigenes Feld. Container pro Bildschirm
 * (`routes/ObjectDetail.tsx`) besitzt die Query, beide Reiter bekommen nur
 * das Ergebnis.
 */
export function useContractCharges(contractId: string, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.billing.contractCharges(contractId),
    queryFn: ({ pageParam, signal }) => {
      const query = pageParam === null ? '' : `?cursor=${encodeURIComponent(pageParam)}`
      return request<ContractChargesPage>(`/billing/contracts/${contractId}/charges${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: options.enabled ?? true,
  })
}

interface GenerateChargesArgs {
  readonly contractId: string
  /** `"YYYY-MM"` aus `<input type="month">` — die Route hängt `-01` an, denn
   * die API erwartet den Monatsersten (Spec 04a, „Rümpfe"). */
  readonly periodMonth: string
}

/** `POST /billing/charges/generate` — „Sollstellung nachziehen"
 * (`charge-generate`), die primäre Aktion des Reiters *Geld* eines
 * Vertrags. */
export function useGenerateCharges() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contractId, periodMonth }: GenerateChargesArgs) =>
      request<ChargesGenerateResponse>('/billing/charges/generate', {
        method: 'POST',
        body: { contract_id: contractId, period_month: `${periodMonth}-01` },
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.billing.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: eine nachgezogene Sollstellung steht als `kind = 'charge'`
      // im Kalenderfenster (W-B2) — dieselbe Zusatz-Invalidierung wie
      // `features/schedule/api.ts` bei jeder Termin-Mutation.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

interface UpdateChargeArgs {
  readonly chargeId: string
  readonly body: ChargeUpdate
  readonly ifMatch: string
}

/** `PATCH /billing/charges/{id}` — die Handkorrektur im Sheet „Sollstellung
 * korrigieren" (`charge-sheet`). */
export function useUpdateCharge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ chargeId, body, ifMatch }: UpdateChargeArgs) =>
      request<ContractCharge>(`/billing/charges/${chargeId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.billing.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: die Handkorrektur ändert Betrag oder Fälligkeit einer
      // Sollstellung im Kalenderfenster (W-B2) — dieselbe Zusatz-
      // Invalidierung wie `features/schedule/api.ts` bei jeder Mutation.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

interface TenantOptionsPage {
  readonly items: TenantOption[]
  readonly next_cursor: string | null
}

/** `GET /tenants?q=` — der Filter „Mieter" (`open-items-filter-tenant`).
 * Ohne Suchtext leer, dieselbe Zurückhaltung wie `TenantPicker`. */
export function useTenantOptions(q: string, options: QueryOptions = {}) {
  const trimmed = q.trim()

  return useQuery({
    // Derselbe Key wie in `features/contracts/api.ts` — kein zweiter,
    // unter `keys.tenants` bereits vorhandener Präfix (Regel 1 aus
    // `lib/query-keys.ts`).
    queryKey: keys.tenants.search(trimmed),
    queryFn: ({ signal }) => request<TenantOptionsPage>(`/tenants?q=${encodeURIComponent(trimmed)}`, { signal }),
    enabled: (options.enabled ?? true) && trimmed !== '',
  })
}

// --- Spec 04b — Zahlungen erfassen und zuordnen -----------------------------

/** `GET /billing/payment-methods` — Enum mit Label für `payment-create-method`,
 * vorbelegt „Überweisung". Ändert sich nie zur Laufzeit, wie
 * `contracts.termReasons`. */
export function usePaymentMethods(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.billing.paymentMethods,
    queryFn: ({ signal }) => request<LabeledValue[]>('/billing/payment-methods', { signal }),
    enabled: options.enabled ?? true,
  })
}

interface CreatePaymentArgs {
  readonly body: PaymentCreate
}

/** `POST /billing/payments` — Zahlung erfassen, Zuordnungen im Rumpf (Spec
 * 04b, „Sheet Zahlung erfassen"). Invalidiert `billing.all` (trifft
 * `open-items` **und** `summary` zugleich, Spec 04b „Was 04b an Bestehendem
 * ändert") und `entities.all` (der Zähl-Endpunkt hinter `rail-geld` hängt
 * ebenfalls an `summary`, aber der Objektkopf kann sich über den neuen
 * Sollstellungsstatus mitändern — dieselbe Doppel-Invalidierung wie
 * `useUpdateCharge`). */
export function useCreatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreatePaymentArgs) =>
      request<PaymentDetail>('/billing/payments', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.billing.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: eine erfasste Zahlung kann den Zustand einer Sollstellung
      // im Kalenderfenster ändern (W-B2) — dieselbe Zusatz-Invalidierung wie
      // `features/schedule/api.ts` bei jeder Mutation.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

interface AddAllocationsArgs {
  readonly paymentId: string
  readonly body: PaymentAllocationsAdd
}

/** `POST /billing/payments/{id}/allocations` — Zuordnungen **ergänzen** (I7,
 * fügt hinzu, ersetzt nie), der dritte Sheet-Einstieg „aus den Klärfällen"
 * (Spec 04c, „Reiter Klärfälle"). Dieselbe Doppel-Invalidierung wie
 * `useCreatePayment`: Eine ergänzte Zuordnung verändert Klärfälle, Mietkonto,
 * offene Posten **und** `summary` zugleich. */
export function useAddAllocations() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ paymentId, body }: AddAllocationsArgs) =>
      request<PaymentDetail>(`/billing/payments/${paymentId}/allocations`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.billing.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: eine ergänzte Zuordnung kann den Zustand einer Sollstellung
      // im Kalenderfenster ändern (W-B2) — dieselbe Zusatz-Invalidierung wie
      // `features/schedule/api.ts` bei jeder Mutation.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

// --- Spec 04c — Storno, Mietkonto und Klärfälle -----------------------------

interface UnallocatedFilter {
  readonly tenantId?: string
  readonly limit?: number
  readonly cursor?: string
  readonly updatedSince?: string
}

/** `GET /billing/unallocated` — Reiter *Klärfälle* (Spec 04c, „Reiter
 * Klärfälle"): Zahlungen mit Rest ≠ 0, `totals` über die ganze gefilterte
 * Menge. */
export function useUnallocated(filter: UnallocatedFilter = {}, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.billing.unallocated({
      tenantId: filter.tenantId,
      limit: filter.limit,
      cursor: filter.cursor,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (filter.tenantId !== undefined) params.set('tenant_id', filter.tenantId)
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (filter.cursor !== undefined) params.set('cursor', filter.cursor)
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<UnallocatedPage>(`/billing/unallocated${query}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

interface LedgerFilter {
  readonly from?: string
  readonly to?: string
  readonly limit?: number
}

/** `GET /billing/tenants/{id}/ledger` — das Mietkonto (Spec 04c,
 * „Mietkonto"): chronologisch, mit laufendem Saldo, über alle Verträge des
 * Mieters. `enabled` schaltet erst mit einer echten `tenantId`, wie
 * `useTenantDetail`. */
export function useTenantLedger(tenantId: string, filter: LedgerFilter = {}, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.billing.ledger(tenantId, {
      from: filter.from,
      to: filter.to,
      limit: filter.limit,
    }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (filter.from !== undefined) params.set('from', filter.from)
      if (filter.to !== undefined) params.set('to', filter.to)
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (pageParam !== null) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<LedgerPage>(`/billing/tenants/${tenantId}/ledger${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    select: (data): LedgerPage => {
      const first = data.pages[0]
      if (first === undefined) throw new Error('Die erste Seite des Mietkontos fehlt.')
      return {
        ...first,
        items: [...new Map(data.pages.flatMap((page) => page.items).map((item) => [item.id, item])).values()],
        next_cursor: data.pages.at(-1)?.next_cursor ?? null,
      }
    },
    enabled: options.enabled ?? true,
  })
}

/** Vormerkbare Monate im Zahlungssheet (Spec 22). */
export function useUpcomingMonths(tenantId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.billing.upcomingMonths(tenantId),
    queryFn: ({ signal }) => request<UpcomingMonthsPage>(`/billing/tenants/${tenantId}/upcoming-months`, { signal }),
    enabled: options.enabled ?? true,
  })
}

interface PrepaidFilter {
  readonly tenantId?: string
  readonly contractId?: string
  readonly scopeId?: string
  readonly updatedSince?: string
}

/** Verträge mit offenen Vormerkungen, cursorpaginiert wie die übrigen Listen. */
export function usePrepaid(filter: PrepaidFilter = {}, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.billing.prepaid({
      tenantId: filter.tenantId,
      contractId: filter.contractId,
      scopeId: filter.scopeId,
      updatedSince: filter.updatedSince,
    }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (filter.tenantId !== undefined) params.set('tenant_id', filter.tenantId)
      if (filter.contractId !== undefined) params.set('contract_id', filter.contractId)
      if (filter.scopeId !== undefined) params.set('scope_id', filter.scopeId)
      if (filter.updatedSince !== undefined) params.set('updated_since', filter.updatedSince)
      if (pageParam !== null) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<PrepaidPage>(`/billing/prepaid${query}`, { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    select: (data): PrepaidPage => {
      const first = data.pages[0]
      if (first === undefined) throw new Error('Die erste Seite der Vorauszahlungen fehlt.')
      return {
        ...first,
        items: [...new Map(data.pages.flatMap((page) => page.items).map((item) => [item.contract.id, item])).values()],
        next_cursor: data.pages.at(-1)?.next_cursor ?? null,
      }
    },
    enabled: options.enabled ?? true,
  })
}

interface ReversePaymentArgs {
  readonly paymentId: string
}

/** `POST /billing/payments/{id}/reverse` — Storno (Spec 04c, „b) Storno").
 * Der Rumpf trägt nur eine optionale `note`, die die Oberfläche nicht abfragt
 * (die Rückfrage im Mietkonto hat kein Eingabefeld, Spec 04c „Mietkonto") —
 * der Aufruf geht deshalb immer ohne Rumpf. Dieselbe Doppel-Invalidierung wie
 * `useCreatePayment`: Ein Storno verändert Klärfälle, Mietkonto, offene
 * Posten **und** `summary` zugleich. */
export function useReversePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ paymentId }: ReversePaymentArgs) =>
      request<PaymentDetail>(`/billing/payments/${paymentId}/reverse`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.billing.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      // Spec 07: ein Storno kann den Zustand einer Sollstellung im
      // Kalenderfenster zurücksetzen (W-B2) — dieselbe Zusatz-Invalidierung
      // wie `features/schedule/api.ts` bei jeder Mutation.
      void queryClient.invalidateQueries({ queryKey: keys.calendar.all })
    },
  })
}

// --- Spec 08 — Zahlungsmatrix -----------------------------------------------

/**
 * `GET /billing/matrix` — Reiter *Zahlungsmatrix* (Spec 08). `months` bleibt
 * am Server-Standard 6 (Fachregel 1, „sechs, gerechnet, nicht gewählt" —
 * siehe „Offene Fragen" der Spec): Der Client übergibt ihn nie.
 *
 * `useInfiniteQuery` statt `useQuery`, weil `matrix-more` Seiten **anhängt**
 * statt zu ersetzen (AK-482) — ein Seitenwechsel über `cursor` ändert dabei
 * nicht den Query-Key, ein Fensterwechsel über `from` (Fensternavigation,
 * AK-485) schon und beginnt deshalb automatisch wieder bei der ersten Seite.
 */
export function useMatrix(from: string | undefined, options: QueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.billing.matrix({ from }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      if (from !== undefined) params.set('from', from)
      if (pageParam !== undefined) params.set('cursor', pageParam)
      const query = params.toString() === '' ? '' : `?${params.toString()}`
      return request<MatrixPage>(`/billing/matrix${query}`, { signal })
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
  })
}
